import React, { ChangeEvent, useEffect } from "react";
import "../../../Pages/Customer/signupdetails/SignUpDetailsScreen.css";
import { kirkos_merchant_profile } from "../../../assets/images";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  CustomCheckBox,
  CustomInput,
  MerchantOnboardLeftSide,
} from "../../../components";
import { FieldValues, Form, useForm } from "react-hook-form";
import rules from "../../../rules";
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { API_URL } from "../../../services/apiConfig";
import axiosInstance from "../../../services/axios.instance";
import { useNavigate } from "react-router-dom";
import { ErrorModal } from "../../../components";

import "./MerchantBusiness.css";
import MerchantProgressbar from "../../../components/MerchantProgressbar";
import { signUpdata, Country, CountryState } from "../../../types";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface LocationData {
  latitude: number;
  longitude: number;
}

const API_LOCATIONIQ_TOKEN = process.env.REACT_APP_LOCATIONIQ_TOKEN;

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface MapClickHandlerProps {
  onClick: (lat: number, lon: number) => void;
}

const MapClickHandler: React.FC<MapClickHandlerProps> = ({ onClick }) => {
  useMapEvents({
    click: (e) => {
      onClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

interface MapUpdaterProps {
  center: [number, number];
}

const MapUpdater: React.FC<MapUpdaterProps> = ({ center }) => {
  const map = useMap();

  React.useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);

  return null;
};

const MerchantBusinessScreen = () => {
  const [country, setCountry] = useState<string>("");
  const [state1, setState1] = useState<string>("");
  const [fetchedBusinessTypes, setFetchedBusinessTypes] = useState<
    { id: string; type: string }[]
  >([]);
  const [selectedBusinessTypeIds, setSelectedBusinessTypeIds] = useState<
    string[]
  >([]);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [businessTypesFetched, setBusinessTypesFetched] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState<string>("");
  const [stateData, setStateData] = useState<CountryState[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [suggestionClicked, setSuggestionClicked] = useState(false);
  const [stateTouched, setStateTouched] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState<string[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<LocationData>({
    latitude: 25.2048,
    longitude: 55.2708,
  });
  const [isLoading, setIsLoading] = useState(false);

  const [mapCenter, setMapCenter] = useState<[number, number]>([25.2048, 55.2708]);
  const [selectedAddressName, setSelectedAddressName] = useState<string>("");
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [mapSearchQuery, setMapSearchQuery] = useState<string>("");
  const [mapSearchResults, setMapSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingCurrentLocation, setIsLoadingCurrentLocation] = useState(false);

  const navigate = useNavigate();

  const enteredData: string = localStorage.getItem("enteredData") as string;
  const parsedData = JSON.parse(enteredData);

  const {
    control,
    formState: { isValid },
    setValue,
    trigger,
    getValues,
  } = useForm({ mode: "onChange" });
  useEffect(() => {
    if (enteredData) {
      console.log("🚀 ~ useEffect ~ parsedData:", parsedData);

      setValue("businessname", parsedData.business_name || "");
      setValue("entityname", parsedData.entity_name || "");
      setValue("email", parsedData.email || "");
      setValue("phoneNum", parsedData.phone || "");
      setValue("fleetnumber", parsedData.number_of_fleets || "");
      setCountry(parsedData.country || "");
      setState1(parsedData.state || "");
      setSelectedBusinessTypeIds(parsedData.business_type || []);
    }
  }, []);

  // Reverse geocode map coordinates using LocationIQ
  const reverseGeocodeMapLocation = async (lat: number, lon: number) => {
    setIsLoadingAddress(true);
    try {
      const response = await fetch(
        `https://us1.locationiq.com/v1/reverse?key=${API_LOCATIONIQ_TOKEN}&lat=${lat}&lon=${lon}&format=json`
      );
      const data = await response.json();
      const addressName = data?.display_name || "Selected Location";

      setSelectedAddressName(addressName);
      setValue("companyAddress", addressName);
      setSuggestionClicked(true);
      setIsLoadingAddress(false);
      return addressName;
    } catch (error) {
      console.error("Error reverse geocoding:", error);
      setIsLoadingAddress(false);
      return "Selected Location";
    }
  };

  // Handle map click to select location
  const handleMapClick = async (lat: number, lon: number) => {
    setSelectedLocation({ latitude: lat, longitude: lon });
    setMapCenter([lat, lon]);
    await reverseGeocodeMapLocation(lat, lon);
  };

  // Search for location using LocationIQ
  const handleMapSearch = async (query: string) => {
    setMapSearchQuery(query);

    if (query.length < 3) {
      setMapSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://us1.locationiq.com/v1/search?key=${API_LOCATIONIQ_TOKEN}&q=${encodeURIComponent(query)}&format=json&limit=5&countrycodes=ae`
      );
      const data = await response.json();
      setMapSearchResults(data || []);
      setIsSearching(false);
    } catch (error) {
      console.error("Error searching location:", error);
      setMapSearchResults([]);
      setIsSearching(false);
    }
  };

  // Handle search result selection
  const handleSearchResultClick = async (result: any) => {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);

    setSelectedLocation({ latitude: lat, longitude: lon });
    setMapCenter([lat, lon]);

    setSelectedAddressName(result.display_name);
    setValue("companyAddress", result.display_name);
    setSuggestionClicked(true);
    setMapSearchQuery("");
    setMapSearchResults([]);
  };

  // Get current location using browser geolocation
  const handleGetCurrentLocationMap = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    setIsLoadingCurrentLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        // Update map location
        setSelectedLocation({ latitude, longitude });
        setMapCenter([latitude, longitude]);

        // Get location name from coordinates
        await reverseGeocodeMapLocation(latitude, longitude);

        setIsLoadingCurrentLocation(false);
      },
      (error) => {
        console.error("Error getting location:", error);
        alert("Unable to retrieve your location. Please enter manually.");
        setIsLoadingCurrentLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleSubmit = async () => {
    trigger();
    setStateTouched(true);
    if (
      !country ||
      !state1 ||
      !isValid ||
      !selectedBusinessTypeIds.length ||
      !suggestionClicked
    ) {
      return;
    }
    let {
      email,
      entityname: entity_name,
      businessname: business_name,
      phoneNum: phone,
      fleetnumber: number_of_fleets,
    }: FieldValues = getValues();
    const userData = {
      business_name,
      entity_name,
      email_address: email,
      number_of_fleets,

      country,
      state: state1,
      business_type: selectedBusinessTypeIds,
    };

    const latitude = selectedLocation?.latitude;
    const longitude = selectedLocation?.longitude;

    localStorage.setItem("enteredData", JSON.stringify(userData));

    handleRegistration.mutate({
      merchant_signup: userData,
      shop_address: {
        address: getValues("companyAddress"),
        latitude: latitude.toString(),
        longitude: longitude.toString(),
        state: state1,
        country: country,
      },
      last_level: 2,
      mobile: phone,
    });

    setIsFormSubmitted(true);
  };

  const handleRegistration = useMutation({
    mutationFn: (userData: signUpdata) => {
      setIsLoading(true);
      return axiosInstance.put(API_URL.SIGNUP_MERCHANT, userData);
    },
    onSuccess: (successResponse) => {
      setIsLoading(false);
      if (successResponse.status && successResponse?.data?.message) {
        navigate("/merchant/signup/licensedetails");
      }
    },
    onError: (error: any) => {
      setIsLoading(false);
      if (error?.data?.message) {
        setErrorMessage(error?.data?.message);
        setShowErrorModal(true);
      }
    },
  });

  const { mutate: fetchBusinessTypes } = useMutation({
    mutationFn: async () => {
      const response = await axiosInstance.get(API_URL.BUSINESS_TYPE);
      return response.data;
    },
    onSuccess: (successResponse) => {
      setFetchedBusinessTypes(successResponse);
    },
    onError: (error: any) => {
      console.error("Error fetching business types:", error);
    },
  });

  if (!businessTypesFetched) {
    fetchBusinessTypes();
    setBusinessTypesFetched(true);
  }

  const handleBusinessTypeChange = (id: string) => {
    setSelectedBusinessTypeIds((prevSelectedIds) => {
      if (prevSelectedIds.includes(id)) {
        return prevSelectedIds.filter((selectedId) => selectedId !== id);
      } else {
        return [...prevSelectedIds, String(id)];
      }
    });
  };
  const { data: countriesData } = useQuery<Country[], Error>({
    queryKey: ["countries"],
    queryFn: async () => {
      const response = await axiosInstance.get(API_URL.COUNTRY);
      return response.data;
    },
  });

  const handleCountryChange = async (e: any) => {
    const selectedCountryName = e.target.value;
    setCountry(selectedCountryName);

    const selectedCountry = countriesData?.find(
      (country) => country.countryName === selectedCountryName
    );

    if (selectedCountry) {
      setSelectedCountryId(selectedCountry.countryId.toString());

      const response = await axiosInstance.get(
        `${API_URL.STATE}${selectedCountry.countryId}`
      );
      setStateData(response.data);
    }
  };

  // const combinedValidationRule = {
  //   validate: (value: any) => {
  //     const isValidPhoneNumber =
  //       /^5\d{8}$/.test(value) ||
  //       /^04-\d{7}$/.test(value) ||
  //       /^04\d{7}$/.test(value);

  //     if (!isValidPhoneNumber) {
  //       return "Invalid  phone number";
  //     }
  //     return true;
  //   },
  // };
  return (
    <div className="outer-container ">
      <Form onSubmit={handleSubmit} control={control}>
        <div className="container-fluid bg-black custom-container ">
          <div className="row onboard-garage-main ">
            <div className="col-md-6 custom-yellow-bg">
              <MerchantOnboardLeftSide />
            </div>

            <div className="col-md-6 custom-grey-bg text-light  text-start msignup-div-right">
              <div className="progressbar-custom ms-3">
                <MerchantProgressbar currentStage={4} />
              </div>
              <div className="row text-start ms-2 mt-5">
                <div className="row">
                  <div className="col-md-12">
                    <img
                      src={kirkos_merchant_profile}
                      className="icon-img img-fluid mb-3"
                      alt="Icon"
                    />
                    <span className="text-light custom-font-signup">
                      {" "}
                      &nbsp;Merchant Sign Up
                    </span>
                  </div>
                  <div className="row">
                    <div className="col-md-10">
                      <p className="mt-1 text-start merchant_business_yellow_text_color">
                        Basic contact information with minimum fields needs to
                        be filled below
                      </p>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="row">
                      <div className="col-md-12 onboard-inner-div-scroll">
                        <div className="col-md-12 ">
                          <label className="text-light mt-3 business_page_right_side_font">
                            Business Name
                            <label className="text-danger ms-0 p-0">*</label>
                          </label>

                          <CustomInput
                            inputName="businessname"
                            inputType="businessname"
                            className="merchantinputStylex"
                            placeholder="Eg: Skyline"
                            editable={true}
                            onInputChange={() => { }}
                            control={control}
                            rules={rules.AuthRules.businessname}
                          />
                        </div>
                        <div className="col-md-12">
                          <label className="text-light mt-3 business_page_right_side_font">
                            Entity Name
                            <label className="text-danger ms-0 p-0">*</label>
                          </label>
                          <CustomInput
                            inputName="entityname"
                            inputType="entityname"
                            editable={true}
                            control={control}
                            className="merchantinputStylex"
                            placeholder="Eg: PVT, LLC..."
                            onInputChange={() => { }}
                            rules={rules.AuthRules.entityname}
                          />
                        </div>

                        <div className="col-md-12">
                          <label className="text-light mt-4 business_page_right_side_font">
                            Email Address{" "}
                            <label className="text-danger ms-0 p-0">*</label>
                          </label>

                          <CustomInput
                            inputName="email"
                            inputType="email"
                            className="merchantinputStylex"
                            placeholder="abc@gmail.com"
                            editable={true}
                            onInputChange={() => { }}
                            control={control}
                            rules={rules.AuthRules.email}
                          />
                        </div>

                        <div className="col-md-12">
                          <label className="text-light mt-3 business_page_right_side_font">
                            Phone Number{" "}
                            {/* <label className="text-danger ms-0 p-0">*</label> */}
                          </label>
                          <div className="inputContainerStyle">
                            {/* <span className="garage_new_country_codestyle ">
                              +971
                            </span> */}
                            <CustomInput
                              inputName="phoneNum"
                              inputType="text"
                              className="merchantinputStylex"
                              placeholder="enter your number"
                              editable={true}
                              onInputChange={() => { }}
                              control={control}
                              disableValidation={true}
                              maxLength={15}
                            // rules={combinedValidationRule}
                            />
                          </div>
                        </div>
                        <div className="col-md-12 ">
                          <label className="text-light pt-3 business_page_right_side_font">
                            Types of Business{" "}
                            <label className="text-danger ms-0 p-0">*</label>
                          </label>

                          <div className="row MerchantrememberMeContainer pt-3">
                            {fetchedBusinessTypes &&
                              fetchedBusinessTypes.map((businessType) => (
                                <React.Fragment key={businessType.id}>
                                  <div className="col-md-6 d-flex">
                                    <CustomCheckBox
                                      isChecked={selectedBusinessTypeIds.includes(
                                        String(businessType.id)
                                      )}
                                      onChange={() =>
                                        handleBusinessTypeChange(
                                          String(businessType.id)
                                        )
                                      }
                                    />
                                    <label className="rememberMeText ">
                                      {businessType.type}
                                    </label>
                                  </div>
                                </React.Fragment>
                              ))}
                          </div>
                          {stateTouched && !selectedBusinessTypeIds.length && (
                            <span className="error-message text-danger d-flex justify-content-end">
                              required
                            </span>
                          )}
                        </div>
                        <div className="col-md-12 pt-3 ">
                          <label className="text-light  business_page_right_side_font">
                            Number of Fleets
                            <label className="text-danger ms-0 p-0">*</label>
                          </label>
                          <CustomInput
                            inputType="Numbers"
                            inputName="fleetnumber"
                            control={control}
                            editable={true}
                            className="merchantinputStylex"
                            placeholder=""
                            onInputChange={() => { }}
                            rules={rules.AuthRules.fleetnumber}
                          />
                        </div>

                        <div className="col-md-12">
                          <div className="row ">
                            <div className="col-md-6">
                              <label className="form-label text-light  mt-4 business_page_right_side_font">
                                Country{" "}
                                <label className="text-danger ms-0 p-0">
                                  *
                                </label>
                              </label>
                              <select
                                className="merchantinputStylex customWidths select-business-style"
                                value={country}
                                onChange={handleCountryChange}
                              >
                                <option
                                  value=""
                                  key="select"
                                  className="business_page_right_side_font"
                                >
                                  Select country
                                </option>
                                {countriesData &&
                                  countriesData.map((country) => (
                                    <option
                                      key={country.countryId}
                                      value={country.countryName}
                                    >
                                      {country.countryName}
                                    </option>
                                  ))}
                              </select>
                              {stateTouched && !country && (
                                <span className="error-message text-danger d-flex justify-content-end">
                                  required
                                </span>
                              )}
                            </div>
                            <div className="col-md-6  ">
                              <label className="form-label text-light mt-4  business_page_right_side_font">
                                State<label className="text-danger ">*</label>
                              </label>
                              <select
                                className="merchantinputStylex customWidths select-business-style"
                                value={state1}
                                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                  setState1(e.target.value)
                                }
                              >
                                <option
                                  value=""
                                  key="select"
                                  className="business_page_right_side_font"
                                >
                                  Select state
                                </option>
                                {stateData &&
                                  stateData.map((state) => (
                                    <option
                                      key={state.id}
                                      value={state.state_name}
                                    >
                                      {state.state_name}
                                    </option>
                                  ))}
                              </select>
                              {stateTouched && !state1 && (
                                <span className="error-message text-danger d-flex justify-content-end">
                                  required
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-12 pt-3 relative-div">
                          <label className="text-light  business_page_right_side_font ">
                            Company Address - Select on Map
                            <label className="text-danger ms-0 p-0">*</label>
                          </label>

                          {/* Search Box with Current Location Button */}
                          <div style={{ position: 'relative', marginTop: '8px', display: 'flex', gap: '8px' }}>
                            <div style={{ flex: 1, position: 'relative' }}>
                              <input
                                type="text"
                                className="merchantinputStylex"
                                placeholder="Search for a location in UAE..."
                                value={mapSearchQuery}
                                onChange={(e) => handleMapSearch(e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px 10px',
                                  borderRadius: '5px',
                                  border: '1px solid #555',
                                  backgroundColor: '#2a2a2a',
                                  color: '#fff',
                                  fontSize: '13px'
                                }}
                              />
                              {isSearching && (
                                <div style={{ position: 'absolute', right: '10px', top: '8px', color: '#FFD700', fontSize: '12px' }}>
                                  Searching...
                                </div>
                              )}

                              {/* Search Results Dropdown */}
                              {mapSearchResults.length > 0 && (
                                <div style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  right: 0,
                                  backgroundColor: '#2a2a2a',
                                  border: '1px solid #555',
                                  borderRadius: '5px',
                                  marginTop: '5px',
                                  maxHeight: '150px',
                                  overflowY: 'auto',
                                  zIndex: 1000
                                }}>
                                  {mapSearchResults.map((result, index) => (
                                    <div
                                      key={index}
                                      onClick={() => handleSearchResultClick(result)}
                                      style={{
                                        padding: '8px',
                                        cursor: 'pointer',
                                        borderBottom: index < mapSearchResults.length - 1 ? '1px solid #444' : 'none',
                                        color: '#fff',
                                        fontSize: '12px'
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
                                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                      📍 {result.display_name}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Current Location Button */}
                            <button
                              type="button"
                              onClick={handleGetCurrentLocationMap}
                              disabled={isLoadingCurrentLocation}
                              style={{
                                padding: '8px 12px',
                                borderRadius: '5px',
                                border: '1px solid #FFD700',
                                backgroundColor: '#2a2a2a',
                                color: '#FFD700',
                                fontSize: '11px',
                                fontWeight: '600',
                                cursor: isLoadingCurrentLocation ? 'not-allowed' : 'pointer',
                                whiteSpace: 'nowrap',
                                minWidth: '130px'
                              }}
                              title="Use current location"
                            >
                              {isLoadingCurrentLocation ? "Loading..." : "📍 Current Location"}
                            </button>
                          </div>

                          {/* LocationIQ Interactive Map */}
                          <div style={{ height: '200px', width: '100%', marginTop: '8px', marginBottom: '8px', borderRadius: '8px', overflow: 'hidden' }}>
                            <MapContainer
                              center={mapCenter}
                              zoom={11}
                              style={{ height: '100%', width: '100%' }}
                              scrollWheelZoom={true}
                              zoomControl={false}
                              attributionControl={false}
                            >
                              <ZoomControl position="bottomright" />
                              <TileLayer
                                attribution=''
                                url={`https://tiles.locationiq.com/v3/streets/r/{z}/{x}/{y}.png?key=${API_LOCATIONIQ_TOKEN}`}
                              />
                              <Marker
                                position={[selectedLocation.latitude, selectedLocation.longitude]}
                                draggable={true}
                                eventHandlers={{
                                  dragend: (e) => {
                                    const marker = e.target;
                                    const position = marker.getLatLng();
                                    handleMapClick(position.lat, position.lng);
                                  },
                                }}
                              />
                              <MapClickHandler onClick={handleMapClick} />
                              <MapUpdater center={mapCenter} />
                            </MapContainer>
                          </div>

                          {/* Selected Address Display */}
                          {isLoadingAddress && (
                            <div className="text-light" style={{ fontSize: '12px', marginBottom: '8px' }}>
                              Loading address...
                            </div>
                          )}
                          {selectedAddressName && !isLoadingAddress && (
                            <div className="text-light" style={{ fontSize: '12px', backgroundColor: '#333', padding: '8px', borderRadius: '5px', marginBottom: '8px' }}>
                              <strong>Selected:</strong> {selectedAddressName}
                            </div>
                          )}

                          {stateTouched && !suggestionClicked && (
                            <span className="error-message text-danger d-flex justify-content-end">
                              Please select a location on the map
                            </span>
                          )}
                        </div>
                        <div className="col-md-12">
                          <button
                            className="btn all_button_style text-black mt-3  mb-3 "
                            type="submit"
                            disabled={isLoading}
                          >
                            SUBMIT
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ErrorModal
            isOpen={showErrorModal}
            onClose={() => setShowErrorModal(false)}
            message={errorMessage}
          />
        </div>
      </Form>
    </div>
  );
};

export default MerchantBusinessScreen;
